import React, { useEffect, useState } from 'react';
import { Typography, DatePicker, Button, Space, Card, message, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const TYPES = [
  { key: 'eval',       label: 'Evaluations',          hint: 'reference date: Call Date'         },
  { key: 'error',      label: 'Error Submissions',     hint: 'reference date: Date of Error'     },
  { key: 'compliance', label: 'Compliance Incidents',  hint: 'reference date: Date of Incident'  },
  { key: 'writeup',    label: 'Formal Write-Ups',      hint: 'reference date: Date of Incident'  },
  { key: 'coaching',   label: 'Coaching Sessions',     hint: 'reference date: Created Date (completed sessions only)' },
];

export default function LockDatesAdminPage() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    api.get('/api/admin/lock-config')
      .then((r) => {
        setConfig(r.data);
        const d = {};
        TYPES.forEach(({ key }) => {
          d[key] = r.data[key] ? dayjs(r.data[key]) : null;
        });
        setDrafts(d);
      })
      .catch(() => message.error('Failed to load lock config.'));
  }, []);

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const val = drafts[key] ? drafts[key].format('YYYY-MM-DD') : '';
      const PRECHECK_LABELS = {
        eval:       'evaluation(s) with a call date',
        error:      'error submission(s) with a date of error',
        compliance: 'compliance incident(s) with a date of incident',
        writeup:    'formal write-up(s) with a date of incident',
      };
      if (val && PRECHECK_LABELS[key]) {
        const check = await api.get(`/api/admin/lock-config/${key}/precheck`, { params: { before: val } });
        if (check.data.count > 0) {
          message.error(
            `There are ${check.data.count} open ${PRECHECK_LABELS[key]} before ${val}. ` +
            `Please close or cancel them before setting this lock date.`,
            6
          );
          return;
        }
      }
      await api.put(`/api/admin/lock-config/${key}`, { lock_before_date: val });
      message.success('Saved.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (!config) return <Spin style={{ display: 'block', marginTop: 40 }} />;

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 8 }}>
        <LockOutlined style={{ marginRight: 8 }} />
        Record Lock Dates
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 580, marginBottom: 24 }}>
        Records whose reference date is strictly before the lock date are protected:
        Set to Canceled and Dispute buttons are hidden, the reference date field
        becomes read-only, and new records cannot be created with a date before the lock date.
        Clear a field to disable locking for that object type.
      </Typography.Paragraph>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {TYPES.map(({ key, label, hint }) => (
          <Card key={key} size="small" style={{ maxWidth: 500 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <Typography.Text strong>{label}</Typography.Text>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{hint}</div>
                <DatePicker
                  value={drafts[key]}
                  onChange={(d) => setDrafts((s) => ({ ...s, [key]: d }))}
                  format="YYYY-MM-DD"
                  placeholder="No lock (disabled)"
                  allowClear
                  style={{ width: 200 }}
                />
              </div>
              <Button type="primary" loading={saving[key]} onClick={() => save(key)}>
                Save
              </Button>
            </div>
          </Card>
        ))}
      </Space>
    </>
  );
}
